import React, { useEffect, useState } from "react";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import HistoryIcon from "@mui/icons-material/History";
import { Button, FormControlLabel, Grid, Switch } from "@mui/material";
import FormDatePicker from "components/FormFields/FormDatePicker";
import FormSelect from "components/FormFields/FormSelect";
import { SUBSCRIPTION_DISABLE_TYPE_REASON_MAPPING } from "static/constants";
import TextsmsOutlinedIcon from "@mui/icons-material/TextsmsOutlined";
import dayjs from "dayjs";
import PauseHistoryDrawer from "components/pages/transmissions/modal/patient/tabs/subscription/pauseHistory/PauseHistoryDrawer";

const SubscriptionControl = ({
  isEdit = false,
  values,
  handleChange,
  isSubmitClicked,
  setIsSubmitClicked,
  isPauseStartDateLocked = false,
}) => {
  const [showPauseHistory, setShowPauseHistory] = useState(false);
  const [currentCommunicationStatus, setCurrentCommunicationStatus] = useState({
    "Disabled Status ": "",
    "Disabled Reason": "",
    "Pause Start Date": "",
    "Pause End Date": "",
  });

  useEffect(() => {
    setCurrentCommunicationStatus({
      "Disabled Status ": values.disabled_type || "",
      "Disabled Reason": values.disabled_reason || "",
      "Pause Start Date": values.pause_start_date || "",
      "Pause End Date": values.pause_end_date || "",
    });
  }, [values]);

  return (
    <div
      style={{
        marginTop: "32px",
        marginBottom: "8px",
        background: "#EAF2FF",
        padding: "20px 30px",
      }}
    >
      <div className="d-flex justify-between" style={{ gap: "16px" }}>
        <div>
          <span
            style={{
              fontWeight: 500,
              fontSize: "16px",
              color: "#175EC8",
            }}
            className="d-flex align-items-center mb-3 "
          >
            <SettingsOutlinedIcon fontSize="small" />
            <span className="ml-2">Subscription Control</span>
          </span>
          <p
            className="patient-form__info--subheader"
            style={{ marginBottom: "16px" }}
          >
            Disabling the subscription will stop all reminders from being sent
            through any notification channel. To temporarily stop reminders, use
            Pause Subscription and select the desired duration. To permanently
            stop reminders, use Remove Subscription.
          </p>
        </div>

        <div className="ml-3">
          <Button
            variant="contained"
            color="secondary"
            startIcon={<HistoryIcon />}
            style={{ whiteSpace: "nowrap" }}
            onClick={() => setShowPauseHistory(true)}
          >
            Pause History
          </Button>
        </div>
      </div>

      <PauseHistoryDrawer
        showDrawer={showPauseHistory}
        handleClose={() => setShowPauseHistory(false)}
      />

      {isEdit && (
        <FormControlLabel
          control={
            <Switch
              name="active"
              color="secondary"
              defaultChecked={values?.disable_configuration || false}
            />
          }
          label="Remove/Pause Subscription"
          labelPlacement="end"
          onChange={(e, c) => handleChange("disable_configuration", c)}
        />
      )}

      {values.disable_configuration === true && (
        <Grid container className="mt-3">
          <span
            style={{
              fontWeight: 500,
              fontSize: "16px",
              color: "#175EC8",
            }}
            className="d-flex align-items-center"
          >
            <TextsmsOutlinedIcon fontSize="small" />
            <span className="ml-2">Communication Status</span>
          </span>
          <Grid container alignItems="center" spacing={1}>
            {isEdit ? (
              <>
                <Grid item xs={3}>
                  <FormSelect
                    label="Status"
                    required
                    margin="none"
                    submitClicked={isSubmitClicked}
                    value={values.disabled_type}
                    options={SUBSCRIPTION_DISABLE_TYPE_REASON_MAPPING.map(
                      ({ type }) => ({ value: type, label: type })
                    )}
                    hasError={
                      isSubmitClicked &&
                      values.disable_configuration &&
                      !values.disabled_type
                    }
                    onChange={(value) => {
                      setIsSubmitClicked(false);
                      handleChange("disabled_type", value);
                    }}
                    sx={{
                      backgroundColor: "#fff",
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "#fff",
                      },
                    }}
                  />
                </Grid>

                <Grid item xs={3}>
                  <FormSelect
                    required
                    margin="none"
                    label="Reason"
                    submitClicked={isSubmitClicked}
                    value={values.disabled_reason}
                    disabled={!values.disabled_type}
                    options={
                      SUBSCRIPTION_DISABLE_TYPE_REASON_MAPPING.find(
                        ({ type }) => type === values.disabled_type
                      )?.reasons?.map((reason) => ({
                        value: reason,
                        label: reason,
                      })) || []
                    }
                    hasError={
                      isSubmitClicked &&
                      values.disable_configuration &&
                      !values.disabled_reason
                    }
                    onChange={(value) => {
                      setIsSubmitClicked(false);
                      handleChange("disabled_reason", value);
                    }}
                    sx={{
                      backgroundColor: "#fff",
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "#fff",
                      },
                    }}
                  />
                </Grid>

                {values.disabled_type === "Paused" && (
                  <>
                    <Grid item xs={2}>
                      <FormDatePicker
                        name="pause_start_date"
                        required
                        disabled={isPauseStartDateLocked}
                        disablePast={!isPauseStartDateLocked}
                        label="Pause Start Date"
                        hasError={isSubmitClicked && !values.pause_start_date}
                        value={values.pause_start_date}
                        maxDate={values.pause_end_date || null}
                        onChange={(date) => {
                          handleChange("pause_start_date", date);
                        }}
                        sx={{
                          backgroundColor: "#fff",
                          "& .MuiOutlinedInput-root": {
                            backgroundColor: "#fff",
                          },
                        }}
                      />
                    </Grid>

                    <Grid item xs={2}>
                      <FormDatePicker
                        name="pause_end_date"
                        required
                        label="Pause End Date"
                        disablePast={true}
                        hasError={isSubmitClicked && !values.pause_end_date}
                        minDate={values.pause_start_date || null}
                        value={values.pause_end_date}
                        onChange={(date) => {
                          handleChange("pause_end_date", date);
                        }}
                        sx={{
                          backgroundColor: "#fff",
                          "& .MuiOutlinedInput-root": {
                            backgroundColor: "#fff",
                          },
                        }}
                      />
                    </Grid>
                  </>
                )}
              </>
            ) : (
              <div className="mt-2 ml-2">
                {Object.entries(currentCommunicationStatus).map(
                  ([key, value]) =>
                    value && (
                      <p key={key} className="mb-1">
                        <b>{key}:</b>{" "}
                        {dayjs.isDayjs(value)
                          ? value.format("MM/DD/YYYY")
                          : String(value)}
                      </p>
                    )
                )}
              </div>
            )}
          </Grid>
        </Grid>
      )}
    </div>
  );
};

export default SubscriptionControl;
